import { useEffect, useRef, useState } from "react";
import { Video, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Student-facing "Watch video solution" for video-eligible questions.
 * Generate-once-and-cache: the first viewer triggers generation (held for
 * teacher approval); once published, everyone streams the same MP4.
 */
export const SolutionVideo = ({ questionId }) => {
  const [state, setState] = useState(null); // {eligible,status,ready,url}
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const pollRef = useRef(null);

  const token = (typeof window !== "undefined" && window.localStorage.getItem("jm_token")) || "";

  const fetchState = async () => {
    try {
      const { data } = await api.get(`/pyq/${questionId}/video`);
      setState(data);
      return data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      const d = await fetchState();
      if ((d && d.status !== "GENERATING") || attempts >= 24) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 5000);
  };

  const request = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post(`/pyq/${questionId}/video`);
      setState(data);
      if (data.status === "GENERATING") {
        toast.info("Generating your video solution — this takes about a minute.");
        startPolling();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start video generation");
    } finally {
      setRequesting(false);
    }
  };

  if (loading || !state || !state.eligible) return null;

  const videoSrc = state.ready ? `${BACKEND}/api/pyq/${questionId}/video/file?auth=${encodeURIComponent(token)}` : null;

  return (
    <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-4" data-testid="solution-video">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Video className="h-4 w-4 text-primary" /> Video solution
      </div>

      {state.ready ? (
        <video
          data-testid="solution-video-player"
          src={videoSrc}
          controls
          className="w-full rounded-lg bg-black"
        />
      ) : state.status === "GENERATING" ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="video-generating">
          <Loader2 className="h-4 w-4 animate-spin" /> Generating your video solution… (~1 minute). You can keep going — it'll be here when you're back.
        </div>
      ) : state.status === "PENDING_REVIEW" ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="video-pending">
          <Clock className="h-4 w-4" /> A video solution is ready and awaiting teacher approval. Check back soon.
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">Want a step-by-step animated walkthrough of this question?</p>
          <Button
            data-testid="generate-video-btn"
            onClick={request}
            disabled={requesting}
            size="sm"
            className="rounded-full"
          >
            {requesting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Video className="mr-1.5 h-4 w-4" />}
            Watch video solution
          </Button>
        </div>
      )}
    </div>
  );
};
